import React from 'react';
import {
    Card,
    Typography,
    Space,
    Button,
    Alert,
    Row,
    Col,
    Spin,
    Switch,
    message,
    Form,
} from 'antd';
import {
    FileProtectOutlined,
    ReloadOutlined,
    SettingOutlined,
    FileTextOutlined,
    UserAddOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
    useGetSystemSettingsByCategoryQuery,
    useUpdateSystemSettingMutation,
    useCreateSystemSettingMutation,
} from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';

const { Text, Paragraph } = Typography;

export const LegalSettingsTab: React.FC = () => {
    const { user } = useAuth();
    const jwtToken = user?.jwtToken || '';

    // RTK Query hooks
    const { data: categorizedData, isLoading: loading, refetch } = useGetSystemSettingsByCategoryQuery({ jwtToken });
    const [updateSetting] = useUpdateSystemSettingMutation();
    const [createSetting] = useCreateSystemSettingMutation();

    // Flatten all settings from categories
    const allSettings = React.useMemo(() => {
        if (!categorizedData?.categories) return [];
        return categorizedData.categories.flatMap(category => category.settings);
    }, [categorizedData]);

    // Get the settings
    const legalAcceptanceEnabled = allSettings.find((s: any) => s.name === 'LEGAL_ACCEPTANCE_ENABLED');
    const publicSignupEnabled = allSettings.find((s: any) => s.name === 'PUBLIC_SIGNUP_ENABLED');

    const handleToggleSetting = async (settingName: string, enabled: boolean, successMessage: string) => {
        try {
            const existingSetting = allSettings.find((s: any) => s.name === settingName);
            if (existingSetting) {
                await updateSetting({
                    jwtToken,
                    settingName,
                    payload: { value: enabled ? 'true' : 'false' },
                }).unwrap();
            } else {
                await createSetting({
                    jwtToken,
                    payload: {
                        name: settingName,
                        value: enabled ? 'true' : 'false',
                        type: 'BOOLEAN',
                    },
                }).unwrap();
            }

            message.success(successMessage);
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to update setting');
        }
    };

    const handleToggleLegalAcceptance = (enabled: boolean) => {
        handleToggleSetting('LEGAL_ACCEPTANCE_ENABLED', enabled, `Legal acceptance modal ${enabled ? 'enabled' : 'disabled'}`);
    };

    const handleTogglePublicSignup = (enabled: boolean) => {
        handleToggleSetting('PUBLIC_SIGNUP_ENABLED', enabled, `Public signup ${enabled ? 'enabled' : 'disabled (closed beta)'}`);
    };

    const isLegalEnabled = legalAcceptanceEnabled?.value === 'true';
    const isSignupEnabled = publicSignupEnabled?.value === 'true';

    return (
        <div>
            <Alert
                message="Legal & Signup Settings"
                description="Control user signup access and legal document acceptance requirements. These settings affect how new users can register and how existing users interact with legal documents."
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Text type="secondary">
                        Manage signup access and legal document requirements
                    </Text>
                </div>
                <Button
                    icon={<ReloadOutlined style={{ marginRight: 4 }} />}
                    onClick={() => refetch()}
                    loading={loading}
                >
                    Refresh Settings
                </Button>
            </div>

            {loading ? (
                <Spin />
            ) : (
                <Row gutter={[24, 24]}>
                    {/* Public Signup Toggle */}
                    <Col xs={24} lg={12}>
                        <Card
                            title={
                                <>
                                    <UserAddOutlined style={{ marginRight: 8 }} />
                                    Public Signup
                                </>
                            }
                            style={{ height: '100%' }}
                        >
                            <Form layout="vertical">
                                <Form.Item
                                    label="Allow Public Signup"
                                    help="When disabled, the signup page shows a 'Closed Beta' message instead of the registration form"
                                >
                                    <Space>
                                        <Switch
                                            checked={isSignupEnabled}
                                            onChange={handleTogglePublicSignup}
                                            checkedChildren="Open"
                                            unCheckedChildren="Closed"
                                        />
                                        <Text type={isSignupEnabled ? 'success' : 'warning'}>
                                            {isSignupEnabled ? 'Anyone can register' : 'Closed beta mode'}
                                        </Text>
                                    </Space>
                                </Form.Item>
                            </Form>

                            {!isSignupEnabled && (
                                <Alert
                                    message="Closed Beta"
                                    description="New users cannot self-register. The signup page displays a closed beta message with contact information."
                                    type="warning"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            )}

                            {isSignupEnabled && (
                                <Alert
                                    message="Public Registration Open"
                                    description="Anyone can create a new account through the signup page. New organizations will be created automatically."
                                    type="success"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            )}
                        </Card>
                    </Col>

                    {/* Legal Acceptance Toggle */}
                    <Col xs={24} lg={12}>
                        <Card
                            title={
                                <>
                                    <SettingOutlined style={{ marginRight: 8 }} />
                                    Legal Document Acceptance
                                </>
                            }
                            style={{ height: '100%' }}
                        >
                            <Form layout="vertical">
                                <Form.Item
                                    label="Require Legal Document Acceptance"
                                    help="When enabled, users will be prompted to accept any updated legal documents upon login"
                                >
                                    <Space>
                                        <Switch
                                            checked={isLegalEnabled}
                                            onChange={handleToggleLegalAcceptance}
                                            checkedChildren="Enabled"
                                            unCheckedChildren="Disabled"
                                        />
                                        <Text type={isLegalEnabled ? 'success' : 'secondary'}>
                                            {isLegalEnabled ? 'Users will be prompted to accept legal documents' : 'Legal acceptance modal is disabled'}
                                        </Text>
                                    </Space>
                                </Form.Item>
                            </Form>

                            {!isLegalEnabled && (
                                <Alert
                                    message="Feature Disabled"
                                    description="Users will not be prompted to accept legal documents when logging in. Enable this setting to enforce acceptance of Privacy Policy and Terms of Service."
                                    type="warning"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            )}

                            {isLegalEnabled && (
                                <Alert
                                    message="Feature Enabled"
                                    description="Users who have not accepted the current version of legal documents will see a modal when they log in."
                                    type="success"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            )}
                        </Card>
                    </Col>

                    {/* Quick Links */}
                    <Col xs={24} lg={12}>
                        <Card
                            title={
                                <>
                                    <FileTextOutlined style={{ marginRight: 8 }} />
                                    Document Management
                                </>
                            }
                            style={{ height: '100%' }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Paragraph>
                                    Manage Privacy Policy and Terms of Service documents, create new versions, and track user acceptance.
                                </Paragraph>

                                <Link to="/admin/legal-documents">
                                    <Button
                                        type="primary"
                                        icon={<FileProtectOutlined style={{ marginRight: 4 }} />}
                                        block
                                        size="large"
                                    >
                                        Manage Legal Documents
                                    </Button>
                                </Link>

                                <Alert
                                    message="How It Works"
                                    description={
                                        <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                                            <li>Create and version legal documents in the Legal Documents page</li>
                                            <li>When a new version is activated, users who haven't accepted it will be prompted</li>
                                            <li>Acceptance is tracked per user, per document version</li>
                                            <li>Users can view documents anytime via footer links</li>
                                        </ul>
                                    }
                                    type="info"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            </Space>
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};
